import axios from "./helpers/axios";
import { Company } from "./types/company";
import { COMPANY_FIRST_CONTACT_FIELD } from "./accountConfig";

export const getCompanyById = (companyId: string): Promise<Company> => {
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.company.get?ID=${companyId}`
      )
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

export const getCompaniesByIds = (
  companyIds: string[],
  accountId: string
): Promise<Company[]> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.company.list`, {
        FILTER: {
          ID: companyIds,
          UF_CRM_PARNERUS_ACCOUNT_ID: accountId,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

/**
 * Busca una empresa ya existente en Bitrix con el mismo nombre, para no duplicar.
 *
 * La migracion dejo pares duplicados (la empresa del cliente ya existia en la base
 * de SF USA Group) y la app los seguia generando: PARNERUS LLC volvio a duplicarse
 * en septiembre, despues de que el equipo la fusionara a mano.
 *
 * Solo se reutiliza si hay UN unico candidato y no pertenece ya a OTRA cuenta de
 * Parnerus: si pertenece a otra, es la empresa de otro cliente con el mismo nombre.
 */
export const findExistingCompany = async (
  title: string,
  accountId: string
): Promise<string | null> => {
  if (!title?.trim()) return null;
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.company.list`,
      { FILTER: { TITLE: title.trim() }, SELECT: ["ID", "UF_CRM_PARNERUS_ACCOUNT_ID"] }
    );
    const matches: { ID: string; UF_CRM_PARNERUS_ACCOUNT_ID?: string }[] =
      response.data.result ?? [];
    // Primero la de esta misma cuenta: donde ya hay un par duplicado (el registro
    // de Parnerus y el de otro negocio), quedarse con el nuestro evita crear un
    // tercero. Si no hay ninguna nuestra, sirve una sin dueño.
    const propias = matches.filter(
      (m) => m.UF_CRM_PARNERUS_ACCOUNT_ID === accountId
    );
    if (propias.length === 1) return propias[0].ID;
    const libres = matches.filter((m) => !m.UF_CRM_PARNERUS_ACCOUNT_ID);
    if (!propias.length && libres.length === 1) return libres[0].ID;
    return null;
  } catch {
    // Que falle la busqueda no puede impedir crear la orden: se sigue de largo.
    return null;
  }
};

export const createCompany = (company: Company): Promise<string> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.company.add`, {
        FIELDS: {
          ...company,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

/**
 * Pone al Mediador de la cuenta como First Contact de una empresa.
 *
 * No se puede mandar en el alta: un business process de la cuenta que corre al
 * crear la empresa deja el campo vacio. Corre durante el alta, asi que un update
 * inmediatamente despues queda firme (verificado a 2, 15 y 45 segundos).
 */
export const setCompanyFirstContact = (
  companyId: string,
  mediadorId: string | number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.company.update`, {
        ID: companyId,
        FIELDS: {
          [COMPANY_FIRST_CONTACT_FIELD]: [mediadorId],
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

export const updateCompany = (company: Partial<Company>): Promise<string> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.company.update`, {
        ID: company.ID,
        FIELDS: {
          ...company,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};
